import { styled, css } from 'styled-components';
import React, { FC, ReactNode } from 'react';
import { Styling } from '@/styling';
import { flexGap } from '@/flexGap';

const Label = styled.label`
  ${Styling.typography('body')}
  color: ${({ theme }) => theme.palette.primary.text};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => flexGap(theme.spacing[1])};
  cursor: pointer;
  user-select: none;
  width: 100%;

  &.disabled {
    cursor: not-allowed;
  }
`;

const Input = styled.input.attrs({
  type: 'radio',
})`
  position: absolute;
  opacity: 0;
  height: 0;
  width: 0;
`;

const SelectorBase = styled.button.attrs({
  type: 'button',
})`
  ${Styling.typography('button')}
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => flexGap(theme.spacing[2])};
  border: 0;
  border-radius: ${Styling.radii('button')};
  cursor: pointer;
  transition:
    color 80ms,
    background-color 80ms,
    border 80ms;
  width: 100%;

  &.lg {
    height: 56px;
    padding: 0 ${Styling.spacing(6)};
  }

  &.md {
    height: 44px;
    padding: 0 ${Styling.spacing(4)};
  }

  &.sm {
    height: 32px;
    padding: 0 ${Styling.spacing(2)};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
  }
`;

export type SelectorProps = {
  className?: string;
  checked?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  text?: ReactNode;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  onChange?(checked: boolean): void;
};

export const Selector: FC<SelectorProps> = ({
  className = '',
  checked,
  onChange,
  size = 'md',
  disabled,
  startAdornment,
  endAdornment,
  text,
  ...props
}) => {
  className += ' ' + size;

  if (checked) {
    className += ' selected';
  }

  return (
    <Label>
      <SelectorBase
        className={className}
        disabled={disabled}
        onClick={() => onChange?.(true)}
      >
        {startAdornment}
        {text}
        {endAdornment}
      </SelectorBase>
      <Input
        disabled={disabled}
        checked={checked}
        onChange={(evt) => onChange?.(evt.target.checked)}
        {...props}
      />
    </Label>
  );
};

type FieldSetProps = {
  columns: number;
};

const FieldSet = styled.fieldset<FieldSetProps>`
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: ${({ columns }) => `repeat(${columns}, 1fr)`};
  gap: ${({ theme }) => theme.spacing[2]};
  border: 0;
  width: 100%;
`;

export type SelectorGroupProps = {
  disabled?: boolean;
  value?: string;
  direction?: 'horizontal' | 'vertical';
  options: {
    value: string;
    label: ReactNode;
    disabled?: boolean;
    startAdornment?: ReactNode;
    endAdornment?: ReactNode;
  }[];
  Component?: FC<SelectorProps>;
  onChange?(selected: string | null): void;
};

export const SelectorGroup: FC<SelectorGroupProps> = ({
  disabled,
  value,
  direction = 'horizontal',
  options,
  onChange,
  Component = TertiarySelector,
}) => {
  const handleChange = (option: string, selected: boolean) => {
    if (selected) {
      onChange?.(option);
    }
  };

  const columns = direction === 'vertical' ? 1 : Math.min(options.length, 3);

  return (
    <FieldSet columns={columns}>
      {options.map((option) => (
        <Component
          key={option.value}
          checked={option.value === value}
          disabled={option.disabled || disabled}
          onChange={(selected) => handleChange(option.value, selected)}
          text={option.label}
          startAdornment={option.startAdornment}
          endAdornment={option.endAdornment}
        />
      ))}
    </FieldSet>
  );
};

const disabledSelectorStyle = css`
  &:disabled {
    cursor: not-allowed;
    background-color: ${({ theme }) => theme.palette.disabled.main};
    color: ${({ theme }) => theme.palette.disabled.text};
    border: 1px solid ${({ theme }) => theme.palette.disabled.dark1};

    &.selected {
      background-color: ${({ theme }) => theme.palette.primary.light5};
    }

    &:hover:not(.selected) {
      background-color: ${({ theme }) => theme.palette.disabled.main};
    }
  }
`;

export const TertiarySelector = styled(Selector)`
  color: ${({ theme }) => theme.palette.primary.text};
  background-color: ${({ theme }) => theme.palette.grayscale.white};
  border: 1px solid ${({ theme }) => theme.palette.grayscale.light3};

  &.selected {
    background-color: ${({ theme }) => theme.palette.primary.light5};
    border: 1px solid ${({ theme }) => theme.palette.primary.main};
  }

  &:not(.selected):hover {
    background-color: ${({ theme }) => theme.palette.primary.light5};
  }

  &:active {
    background-color: ${({ theme }) => theme.palette.primary.light5};
  }

  ${disabledSelectorStyle}
`;
